import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authFetch } from '../services/authFetch';
import { readGuarded, writeGuarded } from '../utils/guardedStorage';
import { useSidebarMode } from '../hooks/useSidebarMode';
import { widthFor, badgeTotal } from '../utils/sidebarLayout';
import Flyout, { menuKeyDown } from './layout/Flyout';
import { useSalesPin } from './layout/salesPin';
import BuildLabel from './layout/BuildLabel';
import { flyoutGroupForNavItem, flyoutSections } from '../modules/registry';
import {
  LayoutDashboard,
  Users,
  Package,
  Image,
  FileText,
  Receipt,
  Warehouse,
  DollarSign,
  BarChart3,
  Brain,
  GraduationCap,
  ShoppingCart,
  ClipboardList,
  Zap,
  Rocket,
  UserCog,
  Wrench,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  TrendingUp,
  FileBarChart,
  Tag,
  ClipboardCheck,
  Truck,
  Shield,
  Database,
  Activity,
  ListChecks,
  AlertTriangle,
  Eye,
  FolderTree,
  History,
  MapPin,
  RotateCcw,
  Pin,
  PinOff
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';

// Navigation items with Lucide icons (exported for tests)
export const navItems = {
  dashboard: { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  customers: { path: '/customers', icon: Users, label: 'Customers' },
  'customers-review-queue': {
    path: '/customers/review-queue', icon: ListChecks, label: 'Review Queue',
    roles: ['admin', 'manager']
  },
  leads: { path: '/leads', icon: ClipboardCheck, label: 'Leads', hasReminderBadge: true },
  'lead-oversight': { path: '/leads/oversight', icon: Eye, label: 'Lead Oversight', roles: ['admin', 'manager', 'supervisor'] },
  products: { path: '/products', icon: Package, label: 'Products' },
  'product-visualization': { path: '/product-visualization', icon: Image, label: 'Product Gallery' },
  quotes: { path: '/quotes', icon: FileText, label: 'Quotations', hasBadge: true },
  // O0 (ROLLOUT-ORDERS-SHELL.md §3 D8): Orders had no nav entry anywhere.
  orders: { path: '/orders', icon: Package, label: 'Orders' },
  'sales-performance': { path: '/sales-performance', icon: TrendingUp, label: 'Sales Performance' },
  invoices: { path: '/invoices', icon: Receipt, label: 'Invoices' },
  'sales-history': { path: '/sales-history', icon: History, label: 'Sales History', roles: ['admin', 'manager'] },
  'permit-leads': { path: '/permit-leads', icon: MapPin, label: 'Permit Leads', roles: ['admin', 'manager'] },
  documents: { path: '/documents', icon: FileText, label: 'Document Centre' },
  inventory: { path: '/inventory', icon: Warehouse, label: 'Inventory' },
  pricing: { path: '/pricing', icon: DollarSign, label: 'Pricing Rules' },
  'manufacturer-promotions': { path: '/manufacturer-promotions', icon: Tag, label: 'Mfr Promotions' },
  insights: { path: '/insights', icon: BarChart3, label: 'Insights' },
  'purchasing-intelligence': { path: '/purchasing-intelligence', icon: Brain, label: 'Purchasing AI' },
  'report-builder': { path: '/report-builder', icon: FileBarChart, label: 'Report Builder' },
  'delivery-schedule': { path: '/delivery-schedule', icon: Truck, label: 'Delivery Schedule' },
  'training-center': { path: '/training-center', icon: GraduationCap, label: 'Training Center' },
  'returns-policy': { path: '/settings/returns-policy', icon: RotateCcw, label: 'Returns Policy' },
  returns: { path: '/returns', icon: RotateCcw, label: 'Returns' },
  marketplace: { path: '/marketplace', icon: ShoppingCart, label: 'Marketplace' },
  reports: { path: '/reports', icon: ClipboardList, label: 'Reports' },
  'bulk-ops': { path: '/bulk-ops', icon: Zap, label: 'Bulk Ops' },
  features: { path: '/features', icon: Rocket, label: '2026 Features', isSpecial: true },
  'delivery-board': { path: '/delivery-board', icon: Truck, label: 'Delivery Board' },
  'delivery-dispatch': { path: '/delivery-dispatch', icon: Truck, label: 'Delivery Dispatch', roles: ['admin', 'manager', 'warehouse'] },
  'admin-deliveries': { path: '/admin/deliveries', icon: Truck, label: 'Delivery Management', roles: ['admin', 'manager'] },
  'admin-users': { path: '/admin/users', icon: UserCog, label: 'User Management', roles: ['admin'] },
  'admin-nomenclature': { path: '/admin/nomenclature', icon: Wrench, label: 'Nomenclature Admin', roles: ['admin'] },
  'admin-category-review': { path: '/admin/category-review', icon: ClipboardList, label: 'Category Review', roles: ['admin'] },
  'admin-phase7-3-review': { path: '/admin/category-review/phase7-3', icon: Brain, label: 'Phase 7.3 Review', roles: ['admin'] },
  'admin-categories': { path: '/admin/categories', icon: FolderTree, label: 'Category Admin', roles: ['admin'] },
  'admin-fraud': { path: '/admin/fraud', icon: Shield, label: 'Fraud & Audit', permissions: ['fraud.alerts.view', 'audit.logs.view'], hasFraudBadge: true },
  'admin-monitoring': { path: '/admin/monitoring', icon: Activity, label: 'Monitoring', permissions: ['errors.client.view'] },
  'admin-data-import': { path: '/admin/data-import', icon: Database, label: 'Data Import', roles: ['admin'] },
  'admin-serial-numbers': { path: '/admin/serial-numbers', icon: Tag, label: 'Serial Numbers', isAdmin: true },
  'admin-purchase-orders': { path: '/admin/purchase-orders', icon: ClipboardList, label: 'Purchase Orders', isAdmin: true },
  'admin-product-variants': { path: '/admin/product-variants', icon: Package, label: 'Product Variants', isAdmin: true },
  // Lightspeed Feature Gap
  'inventory-transfers': { path: '/inventory/transfers', icon: Truck, label: 'Transfers' },
  'inventory-receiving': { path: '/inventory/receiving', icon: Package, label: 'Receiving' },
  'inventory-counts': { path: '/inventory-counts', icon: ClipboardCheck, label: 'Inventory Counts' },
  'count-review': { path: '/inventory/count-review', icon: ListChecks, label: 'Count Review' },
  'manufacturer-ras': { path: '/manufacturer-ras', icon: Truck, label: 'Manufacturer RAs', isAdmin: true },
  'work-orders': { path: '/work-orders', icon: Wrench, label: 'Work Orders', permissions: ['work_orders.view'] },
  'special-orders': { path: '/special-orders', icon: ShoppingCart, label: 'Special Orders' },
  'backorders': { path: '/backorders', icon: AlertTriangle, label: 'Backorders' },
  'customer-accounts': { path: '/customer-accounts', icon: DollarSign, label: 'Customer Accounts' },
  'pre-orders': { path: '/pre-orders', icon: ClipboardList, label: 'Pre-Orders' },
  'team-commissions': { path: '/team-commissions', icon: DollarSign, label: 'Team Commissions' },
  'surveys': { path: '/surveys', icon: ClipboardCheck, label: 'Surveys & Reviews' },
  'catalog-exports': { path: '/catalog-exports', icon: Database, label: 'Catalog Exports' },
  'audience-sync': { path: '/audience-sync', icon: Users, label: 'Audience Sync' },
};

// Section configuration
const navSections = [
  {
    id: 'analytics',
    title: 'Analytics',
    icon: BarChart3,
    items: ['dashboard', 'insights', 'purchasing-intelligence', 'report-builder', 'delivery-schedule']
  },
  {
    id: 'sales',
    title: 'Sales',
    icon: Users,
    items: ['customers', 'customers-review-queue', 'leads', 'lead-oversight', 'quotes', 'orders', 'sales-performance', 'team-commissions', 'invoices', 'sales-history', 'returns', 'permit-leads', 'documents', 'special-orders', 'backorders', 'customer-accounts', 'pre-orders', 'delivery-board']
  },
  {
    id: 'inventory',
    title: 'Inventory',
    icon: Package,
    items: ['products', 'admin-product-variants', 'product-visualization', 'inventory', 'inventory-transfers', 'inventory-receiving', 'inventory-counts', 'count-review', 'admin-serial-numbers', 'admin-purchase-orders', 'manufacturer-ras', 'pricing', 'manufacturer-promotions']
  },
  {
    id: 'marketplace',
    title: 'Marketplace',
    icon: ShoppingCart,
    items: ['marketplace', 'bulk-ops', 'reports', 'catalog-exports', 'audience-sync', 'surveys'],
    requiresPermission: 'marketplace'
  },
  {
    id: 'tools',
    title: 'Tools',
    icon: Settings,
    items: ['training-center', 'features', 'returns-policy']
  }
];

/**
 * The Admin section (F2, 2026-10-27). It used to be added whole when users.role === 'admin'.
 * Each item is now gated on the same thing as the backend route behind its page, so a user sees
 * exactly the items their role holds, and the section shows when one of them does:
 *
 *   delivery-dispatch      roles admin, manager, warehouse   deliveries.js EDIT_ROLES (the page's
 *                                                            edits; its reads are sign-in only)
 *   admin-deliveries       roles admin, manager              the page's ProtectedRoute; its load
 *                                                            URLs reach no gated route
 *   admin-users            roles admin                       users.js: every write is admin
 *   admin-nomenclature     roles admin                       nomenclature.js: actions admin by name
 *   admin-category-review  roles admin                       products.js requireRole('admin')
 *   admin-phase7-3-review  roles admin                       products.js requireRole('admin')
 *   admin-categories       roles admin                       admin/categories.js requireRole('admin')
 *   admin-fraud            fraud.alerts.view | audit.logs.view   fraud.js / audit.js
 *   admin-monitoring       errors.client.view                client-errors.js (landing tab)
 *   admin-data-import      roles admin                       admin/skulytics.js admin by name
 *   work-orders            work_orders.view                  work-orders.js
 *
 * An item's `roles` is matched against users.role; its `permissions` against the codes
 * GET /api/auth/me returned (AuthContext), any of them, an admin holding every code by name.
 */
export const ADMIN_SECTION_ITEMS = ['delivery-dispatch', 'admin-deliveries', 'admin-users', 'admin-nomenclature', 'admin-category-review', 'admin-phase7-3-review', 'admin-categories', 'admin-fraud', 'admin-monitoring', 'admin-data-import', 'work-orders'];

const adminSection = {
  id: 'admin',
  title: 'Admin',
  icon: UserCog,
  items: ADMIN_SECTION_ITEMS,
  // Every item has a gate, and a user with no role passes none of them.
  strictGates: true
};

/**
 * Whether the user may see a nav item. `roles`: users.role is in the list (a user with no role
 * passes only outside a strict section, as before). `permissions`: an admin, or any code held.
 */
const itemVisibleFor = (item, { role, isAdmin = false, permissions = [] } = {}, strict = false) => {
  if (!item) return false;
  if (item.roles) {
    if (!role) return !strict;
    if (!item.roles.includes(role.toLowerCase())) return false;
  }
  if (item.permissions) {
    if (isAdmin) return true;
    const held = Array.isArray(permissions) ? permissions : [];
    return item.permissions.some((code) => held.includes(code));
  }
  return true;
};

// The sections this user sees: the marketplace section needs its permission; the Admin section
// shows when any of its items does.
const sectionsFor = ({ isAdmin, canAccessMarketplace, role, permissions }) => {
  const result = navSections.filter(s =>
    s.requiresPermission !== 'marketplace' || canAccessMarketplace
  );
  const who = { role, isAdmin, permissions };
  if (adminSection.items.some((id) => itemVisibleFor(navItems[id], who, true))) result.push(adminSection);
  return result;
};

/**
 * The sidebar's modules as one list, gated exactly as the sidebar gates them, in its order —
 * the command palette's Navigate group reads it (S6). [{ id, path, label, section }]
 * `permissions` are the codes from /me (F2); a caller that passes none gets the role gates only.
 */
export const navEntriesFor = ({ isAdmin, canAccessMarketplace, role, permissions = [] }) => {
  const who = { role, isAdmin, permissions };
  return sectionsFor({ isAdmin, canAccessMarketplace, role, permissions }).flatMap((section) => section.items
    .map((id) => ({ id, item: navItems[id] }))
    .filter(({ item }) => itemVisibleFor(item, who, !!section.strictGates))
    .map(({ id, item }) => ({ id, path: item.path, label: item.label, section: section.title })));
};

/**
 * 10x Sidebar Navigation Component
 * Features: Lucide icons, collapsible sections, smooth animations
 */
// sidebar_sections is only ever written as a plain object of booleans
// (section id → expanded). Key-set tolerant so adding a section later
// doesn't invalidate stored state.
const isSectionMap = (value) =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  Object.values(value).every((v) => typeof v === 'boolean');

// Pure section-tap mapping for rail mode: returns a new section map with
// sectionId expanded, preserving every other section's state. Exported for
// unit tests.
export const openSection = (sections, sectionId) => ({ ...sections, [sectionId]: true });

// Rail sections whose tap opens the Z3 flyout instead of expanding the sidebar
// (PILOT-QUOTES-SHELL.md §4.3: Sales only in the pilot). The tap neither toggles the
// mode nor writes crm.ui.sidebar.v1; the others keep expand-and-persist until they get one.
// G0: the sections come from the module registry's flyout groups (Quotes' is 'sales').
export const FLYOUT_SECTIONS = flyoutSections();

// A pinned flyout (§4.2, S4b) is a column that squeezes the page by 240 px. It is offered only
// where the page still fits beside it — rail mode at W - 240 >= 941, i.e. W >= 1181 (a 1440
// desktop, not the tablet) — and it is the user's: shell.flyout.pinned in their document, with
// the pre-G0 quotes.flyout.pinned as the fallback (layout/salesPin.js). The pin is the first
// flyout section's (Sales).
export const PIN_MIN_WIDTH = 1181;
export const PINNED_WIDTH = 240;

const Sidebar = ({ children, isLayoutMode = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);
  const [fraudAlertCount, setFraudAlertCount] = useState(0);
  const [reminderCount, setReminderCount] = useState(0);
  const [expandedSections, setExpandedSections] = useState(() =>
    // Load from localStorage or default all expanded. Guarded read: a corrupt
    // or wrong-shaped value falls back to defaults and removes the bad key.
    readGuarded(
      'sidebar_sections',
      isSectionMap,
      { analytics: true, sales: true, inventory: true, marketplace: true, tools: true, admin: true }
    )
  );
  const location = useLocation();
  const { user, isAdmin, canApproveQuotes, canAccessMarketplace, permissions: heldPermissions } = useAuth();
  const permissions = useMemo(() => (Array.isArray(heldPermissions) ? heldPermissions : []), [heldPermissions]);
  // The fraud badge reads GET /api/fraud/scores/unreviewed-count, gated fraud.alerts.view.
  const canSeeFraudCount = isAdmin || permissions.includes('fraud.alerts.view');
  const { mode, isRail, toggle } = useSidebarMode();
  // The rail section whose flyout is open (S3), and its rail button (focus returns there).
  const [flyoutSection, setFlyoutSection] = useState(null);
  const flyoutTriggerRef = useRef(null);
  // The pinned Sales flyout (S4b): the user's choice, shown only where it fits.
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const salesPin = useSalesPin();
  const pinnedMenuRef = useRef(null);
  // Expanded mode: a module's views shown under its nav row (S3, §4.3), by nav item. Not persisted.
  const [openViews, setOpenViews] = useState({});

  // Save expanded sections to localStorage (guarded: never throws on
  // quota/private-mode failures — persistence is best-effort)
  useEffect(() => {
    writeGuarded('sidebar_sections', expandedSections);
  }, [expandedSections]);

  // Fetch pending approvals count
  const fetchPendingApprovals = useCallback(async () => {
    if (!canApproveQuotes) return;
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;
      const response = await authFetch(`${API_URL}/api/counter-offers/pending`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setPendingApprovalsCount(data.data?.counterOffers?.length || 0);
      }
    } catch (err) {
      console.error('Error fetching pending approvals:', err);
    }
  }, [canApproveQuotes]);

  useEffect(() => {
    fetchPendingApprovals();
    const interval = setInterval(fetchPendingApprovals, 60000);
    return () => clearInterval(interval);
  }, [fetchPendingApprovals]);

  // Fetch unreviewed fraud alert count + WebSocket listener
  useEffect(() => {
    if (!canSeeFraudCount) return;

    const fetchFraudCount = async () => {
      try {
        const res = await authFetch(`${API_URL}/api/fraud/scores/unreviewed-count`);
        const data = await res.json();
        if (data.success) {
          setFraudAlertCount(data.data?.count || 0);
        }
      } catch { /* ignore */ }
    };

    fetchFraudCount();
    const interval = setInterval(fetchFraudCount, 120000);

    // WebSocket for real-time fraud alert badge updates (with exponential backoff)
    const wsUrl = API_URL.replace(/^http/, 'ws');
    const token = localStorage.getItem('auth_token');
    let ws = null;
    let reconnectTimer = null;
    let reconnectDelay = 5000;
    const MAX_RECONNECT_DELAY = 120000; // cap at 2 minutes

    const connectWs = () => {
      if (!token) return;
      try {
        ws = new WebSocket(`${wsUrl}/ws?token=${token}`);
        ws.onopen = () => {
          reconnectDelay = 5000; // reset backoff on successful connection
        };
        ws.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data);
            if (msg.event === 'fraud:alert') {
              setFraudAlertCount(prev => prev + 1);
            }
          } catch { /* ignore */ }
        };
        ws.onclose = () => {
          reconnectTimer = setTimeout(connectWs, reconnectDelay);
          reconnectDelay = Math.min(reconnectDelay * 2, MAX_RECONNECT_DELAY);
        };
        ws.onerror = () => { /* onclose will fire after this */ };
      } catch {
        reconnectTimer = setTimeout(connectWs, reconnectDelay);
        reconnectDelay = Math.min(reconnectDelay * 2, MAX_RECONNECT_DELAY);
      }
    };

    connectWs();

    return () => {
      clearInterval(interval);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws) ws.close();
    };
  }, [canSeeFraudCount]);

  // Fetch lead reminder count (30-second poll)
  useEffect(() => {
    const fetchReminderCount = async () => {
      try {
        const res = await authFetch(`${API_URL}/api/leads/reminders/count`);
        const data = await res.json();
        if (data.success) {
          setReminderCount(data.data?.count || 0);
        }
      } catch { /* ignore */ }
    };

    fetchReminderCount();
    const interval = setInterval(fetchReminderCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // The unassigned-customers badge poll was removed here (2026-09-28).
  //
  // It hit GET /api/customers/unassigned/count every 60 s for every admin and manager,
  // and every call returned 404 — not a wrong URL, and not a missing route. The route
  // exists at exactly that path (routes/customers.js:471, correctly ordered before /:id);
  // its `requireFlagOn` guard answers 404 while the `customer_location_ownership` flag is
  // off, which is how that feature is deliberately kept off-radar during rollout
  // (config/feature-flags.js:43 defaults it false).
  //
  // The 404 was already swallowed and the badge only renders above zero, so the poll
  // could never show anything: it was a request per minute per session for a count that
  // was always 0. The /customers/review-queue nav item and ReviewQueuePage stay — that
  // page handles flag-off with a real message to the user.
  //
  // To restore when the flag is switched on: re-add the effect, the `unassignedCount`
  // state, `hasReviewQueueBadge: true` on the nav item, the badge span, and the
  // `unassignedCount` term in railCounts.sales.

  // Close sidebar on route change (mobile)
  useEffect(() => {
    if (isMobile) setIsOpen(false);
  }, [location.pathname, isMobile]);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (!mobile) setIsOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent body scroll when sidebar is open
  useEffect(() => {
    document.body.style.overflow = isOpen && isMobile ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen, isMobile]);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  // Build sections list based on role and permissions
  const sections = useMemo(
    () => sectionsFor({ isAdmin, canAccessMarketplace, role: user?.role, permissions }),
    [isAdmin, canAccessMarketplace, user?.role, permissions]
  );

  // Styles
  const styles = {
    navLink: (isActive, isSpecial = false) => ({
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '12px 16px',
      marginLeft: '12px',
      borderRadius: '8px',
      textDecoration: 'none',
      fontSize: '14px',
      fontWeight: isActive ? '600' : '500',
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      background: isActive
        ? isSpecial
          ? 'linear-gradient(135deg, var(--status-success-solid) 0%, var(--status-success-solid) 100%)'
          : 'linear-gradient(135deg, var(--brand-gradient-start) 0%, var(--brand-gradient-end) 100%)'
        : 'transparent',
      color: isActive ? 'var(--text-on-accent)' : 'var(--text-tertiary)',
      boxShadow: isActive
        ? '0 4px 15px rgba(102, 126, 234, 0.35), inset 0 1px 0 rgba(255,255,255,0.15)'
        : 'none',
      transform: isActive ? 'translateX(4px)' : 'translateX(0)',
      borderLeft: isActive ? 'none' : '3px solid transparent',
    }),
    navLinkHover: {
      background: 'var(--bg-hover)',
      transform: 'translateX(4px)',
    },
    sectionHeader: () => ({
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '10px 16px',
      cursor: 'pointer',
      userSelect: 'none',
      fontSize: '11px',
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      // --text-tertiary, not --text-muted (2.54:1 light, 3.04:1 dark): 4.83 / 5.78; hovered, on
      // --bg-hover, --text-secondary (8.33 / 6.10).
      color: 'var(--text-tertiary)',
      transition: 'all 0.2s ease',
      borderRadius: '6px',
      margin: '4px 8px',
    }),
    sectionContent: (isExpanded) => ({
      overflow: 'hidden',
      maxHeight: isExpanded ? '2000px' : '0',
      opacity: isExpanded ? 1 : 0,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    }),
    badge: {
      background: 'linear-gradient(135deg, var(--status-error-solid) 0%, var(--status-error-solid) 100%)',
      color: 'var(--text-on-accent)',
      fontSize: '10px',
      fontWeight: 'bold',
      padding: '2px 6px',
      borderRadius: '6px',
      minWidth: '18px',
      textAlign: 'center',
      boxShadow: '0 2px 4px rgba(239, 68, 68, 0.3)',
      animation: 'pulse 2s infinite',
    },
    icon: () => ({
      width: '18px',
      height: '18px',
      strokeWidth: 2,
      flexShrink: 0,
      transition: 'transform 0.2s ease',
    }),
  };

  const isItemVisible = (item) => itemVisibleFor(
    item, { role: user?.role, isAdmin, permissions }, ADMIN_SECTION_ITEMS.some((id) => navItems[id] === item));

  const itemAriaLabel = (item) => (item.hasBadge && canApproveQuotes && pendingApprovalsCount > 0
    ? `${item.label}, ${pendingApprovalsCount} pending approvals`
    : item.hasFraudBadge && canSeeFraudCount && fraudAlertCount > 0
    ? `${item.label}, ${fraudAlertCount} unreviewed alerts`
    : item.label);

  // Render nav item
  const renderNavItem = (itemKey) => {
    const item = navItems[itemKey];
    if (!isItemVisible(item)) return null;
    const Icon = item.icon;
    // A nav item a module nests views under (the registry's flyout groups; Quotations today).
    const viewsGroup = flyoutGroupForNavItem(itemKey);
    const hasViews = !!viewsGroup;
    const viewsOpen = hasViews && openViews[itemKey] === true;

    const link = (
        <NavLink
          to={item.path}
          role="menuitem"
          aria-label={itemAriaLabel(item)}
          style={({ isActive }) => ({ ...styles.navLink(isActive, item.isSpecial), ...(hasViews ? { flex: 1 } : null) })}
          onMouseEnter={(e) => {
            if (!e.currentTarget.classList.contains('active')) {
              Object.assign(e.currentTarget.style, styles.navLinkHover);
            }
          }}
          onMouseLeave={(e) => {
            const isActive = location.pathname === item.path;
            if (!isActive) {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.transform = 'translateX(0)';
            }
          }}
        >
          <Icon style={styles.icon(location.pathname === item.path)} />
          <span style={{ flex: 1 }}>{item.label}</span>
          {item.hasBadge && canApproveQuotes && pendingApprovalsCount > 0 && (
            <span style={styles.badge}>{pendingApprovalsCount}</span>
          )}
          {item.hasFraudBadge && canSeeFraudCount && fraudAlertCount > 0 && (
            <span style={styles.badge}>{fraudAlertCount > 99 ? '99+' : fraudAlertCount}</span>
          )}
          {item.hasReminderBadge && reminderCount > 0 && (
            <span style={{
              ...styles.badge,
              background: 'linear-gradient(135deg, var(--status-negotiating) 0%, var(--status-negotiating-border) 100%)',
              boxShadow: '0 2px 4px rgba(200, 97, 74, 0.3)'
            }}>
              {reminderCount > 99 ? '99+' : reminderCount}
            </span>
          )}
        </NavLink>
    );

    if (!hasViews) {
      return <li key={item.path} role="none">{link}</li>;
    }

    // A module's views as an expandable child list (S3, §4.3) — the flyout's component.
    const { ViewsMenu } = viewsGroup;
    return (
      <li key={item.path} role="none">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {link}
          <button
            type="button"
            aria-label={viewsOpen && viewsGroup.hideLabel ? viewsGroup.hideLabel : viewsGroup.viewsLabel}
            aria-expanded={viewsOpen}
            onClick={() => setOpenViews((open) => ({ ...open, [itemKey]: !open[itemKey] }))}
            style={{
              width: '36px',
              height: '36px',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'transparent',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              color: 'var(--text-tertiary)',
            }}
          >
            {viewsOpen
              ? <ChevronDown style={{ width: '16px', height: '16px' }} />
              : <ChevronRight style={{ width: '16px', height: '16px' }} />}
          </button>
        </div>
        {viewsOpen && <ViewsMenu inline />}
      </li>
    );
  };

  // A page in a rail flyout: the expanded row's gate and name; a module's nav item nests its
  // views (the registry's flyout groups — Quotations today).
  const renderFlyoutItem = (itemKey) => {
    const item = navItems[itemKey];
    if (!isItemVisible(item)) return null;
    const Icon = item.icon;
    const viewsGroup = flyoutGroupForNavItem(itemKey);
    const ViewsMenu = viewsGroup ? viewsGroup.ViewsMenu : null;
    return (
      <li key={item.path} role="none">
        <NavLink to={item.path} role="menuitem" aria-label={itemAriaLabel(item)} className="shell-flyout-item">
          <Icon aria-hidden="true" />
          <span style={{ flex: 1 }}>{item.label}</span>
        </NavLink>
        {ViewsMenu && (
          <div style={{ paddingLeft: '12px' }}>
            <ViewsMenu />
          </div>
        )}
      </li>
    );
  };

  // Render section
  const renderSection = (section) => {
    const isExpanded = expandedSections[section.id];
    const SectionIcon = section.icon;

    return (
      <div key={section.id} style={{ marginBottom: '8px' }}>
        <div
          style={styles.sectionHeader(isExpanded)}
          onClick={() => toggleSection(section.id)}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-tertiary)'; }}
          role="button"
          aria-expanded={isExpanded}
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleSection(section.id); }}
        >
          <SectionIcon style={{ width: '14px', height: '14px', strokeWidth: 2.5 }} />
          <span style={{ flex: 1 }}>{section.title}</span>
          {isExpanded ? (
            <ChevronDown style={{ width: '14px', height: '14px', transition: 'transform 0.2s' }} />
          ) : (
            <ChevronRight style={{ width: '14px', height: '14px', transition: 'transform 0.2s' }} />
          )}
        </div>
        <div style={styles.sectionContent(isExpanded)}>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} role="menu">
            {section.items.map(renderNavItem)}
          </ul>
        </div>
      </div>
    );
  };

  // Sidebar content
  const sidebarContent = (
    <>
      {/* Navigation Sections */}
      <nav id="sidebar-primary-nav" aria-label="Primary navigation" style={{ padding: '12px 4px', flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {sections.map(renderSection)}
      </nav>

      {/* Footer */}
      <div style={{
        padding: '16px 20px',
        borderTop: '1px solid var(--border-color)',
        fontSize: '11px',
        // --text-tertiary (was --text-muted, and the version faded further to 70%): 4.63:1
        // light, 5.78:1 dark on --bg-secondary.
        color: 'var(--text-tertiary)',
        background: 'linear-gradient(to top, var(--bg-secondary) 0%, rgba(255,255,255,0) 100%)',
      }}>
        <div style={{ fontWeight: '600' }}>TeleTime Solutions</div>
        {/* The running bundle (polish 4; it was a static version string). */}
        <BuildLabel />
      </div>
    </>
  );

  const canPin = isRail && viewportWidth >= PIN_MIN_WIDTH;
  const showPinned = canPin && salesPin.pinned;
  const setPinned = (pinned) => {
    salesPin.setPinned(pinned);
    setFlyoutSection(null);
    if (flyoutTriggerRef.current) flyoutTriggerRef.current.focus();
  };

  // Rail mode: one ≥48px icon+label target per section, no section children,
  // no footer. Counts are raw state — each is 0 unless its permission-gated
  // fetch ran, so no additional gating is needed here.
  const railCounts = {
    sales: badgeTotal([pendingApprovalsCount, reminderCount]),
    admin: badgeTotal([fraudAlertCount]),
  };

  // Derived from styles.badge: colours/shadow/variant inherited, geometry
  // overridden for the icon-overlay bubble. The pulse declaration is inert
  // (no @keyframes pulse mounts in the live branch) — kept as-is per ruling.
  const railBadgeStyle = {
    ...styles.badge,
    position: 'absolute',
    top: '-4px',
    right: '-10px',
    minWidth: '16px',
    height: '16px',
    lineHeight: '16px',
    padding: '0 4px',
    borderRadius: '8px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const railContent = (
    <nav
      id="sidebar-primary-nav"
      aria-label="Primary navigation"
      style={{ padding: '12px 0', flex: 1, minHeight: 0, overflowY: 'auto' }}
    >
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {sections.map((section) => {
          const SectionIcon = section.icon;
          const sum = railCounts[section.id] || 0;
          const hasFlyout = FLYOUT_SECTIONS.includes(section.id);
          return (
            <li key={section.id}>
              <button
                type="button"
                ref={hasFlyout ? flyoutTriggerRef : undefined}
                aria-haspopup={hasFlyout ? 'menu' : undefined}
                aria-expanded={hasFlyout ? showPinned || flyoutSection === section.id : undefined}
                onClick={() => {
                  if (hasFlyout && showPinned) {
                    // Already open beside the rail: go to its first entry.
                    const first = pinnedMenuRef.current && pinnedMenuRef.current.querySelector('[role="menuitem"]');
                    if (first) first.focus();
                    return;
                  }
                  if (hasFlyout) {
                    setFlyoutSection((open) => (open === section.id ? null : section.id));
                    return;
                  }
                  setExpandedSections((prev) => openSection(prev, section.id));
                  toggle();
                }}
                aria-label={sum > 0 ? `${section.title}, ${sum} pending` : section.title}
                style={{
                  width: '100%',
                  minHeight: '58px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '2px',
                  padding: '10px 0',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--text-tertiary)',
                  fontSize: '10px',
                  fontWeight: 600,
                }}
              >
                <span style={{ position: 'relative', display: 'inline-flex' }}>
                  <SectionIcon style={{ width: '24px', height: '24px' }} />
                  {sum > 0 && (
                    <span style={railBadgeStyle}>{sum > 99 ? '99+' : sum}</span>
                  )}
                </span>
                <span>{section.title}</span>
              </button>
            </li>
          );
        })}
      </ul>
      {!showPinned && sections.filter((section) => section.id === flyoutSection).map((section) => (
        <Flyout
          key={section.id}
          open
          label={section.title}
          triggerRef={flyoutTriggerRef}
          onClose={() => setFlyoutSection(null)}
          header={canPin ? (
            <div className="shell-flyout-pinbar">
              <span className="shell-flyout-heading">{section.title}</span>
              <button
                type="button"
                className="shell-flyout-pin"
                aria-pressed="false"
                aria-label="Pin the Sales menu"
                title="Keep this menu open beside the rail"
                onClick={() => setPinned(true)}
              >
                <Pin aria-hidden="true" />
              </button>
            </div>
          ) : null}
        >
          <ul className="shell-flyout-list" role="none">
            {section.items.map(renderFlyoutItem)}
          </ul>
        </Flyout>
      ))}
    </nav>
  );

  // Collapse/expand toggle — pinned outside the scrolling nav so it stays reachable however
  // far the nav overflows (in rail mode it is the only way back to expanded).
  const modeToggle = (
    <div
      style={{
        flexShrink: 0,
        display: 'flex',
        justifyContent: isRail ? 'center' : 'flex-end',
        padding: '4px',
        borderBottom: '1px solid var(--border-light)',
      }}
    >
      <button
        type="button"
        onClick={toggle}
        aria-expanded={!isRail}
        aria-controls="sidebar-primary-nav"
        aria-label={isRail ? 'Expand sidebar' : 'Collapse sidebar'}
        style={{
          width: '48px',
          height: '48px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'transparent',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          color: 'var(--text-tertiary)',
        }}
      >
        {isRail ? <PanelLeftOpen size={20} /> : <PanelLeftClose size={20} />}
      </button>
    </div>
  );

  // The pinned Sales flyout: the same entries as the overlay, as a column beside the rail.
  const salesSection = sections.find((section) => section.id === FLYOUT_SECTIONS[0]);
  const pinnedColumn = showPinned && salesSection ? (
    <section aria-label={`${salesSection.title}, pinned`} className="shell-pinned">
      <div className="shell-flyout-pinbar">
        <span className="shell-flyout-heading">{salesSection.title}</span>
        <button
          type="button"
          className="shell-flyout-pin"
          aria-pressed="true"
          aria-label="Unpin the Sales menu"
          title="Close this column; Sales opens as a flyout again"
          onClick={() => setPinned(false)}
        >
          <PinOff aria-hidden="true" />
        </button>
      </div>
      <div
        ref={pinnedMenuRef}
        role="menu"
        aria-label={salesSection.title}
        onKeyDown={(e) => menuKeyDown(e, pinnedMenuRef.current)}
      >
        <ul className="shell-flyout-list" role="none">
          {salesSection.items.map(renderFlyoutItem)}
        </ul>
      </div>
    </section>
  ) : null;

  // Layout Mode: Render only the sidebar panel. The shell is viewport-locked (polish 4,
  // layout/Shell.css): the panel stretches to the row under the header and clips; its nav
  // scrolls within it, so opening the inline views never makes the page taller.
  if (isLayoutMode) {
    return (
      <aside
        role="navigation"
        aria-label="Main navigation"
        style={{
          alignSelf: 'stretch',
          minHeight: 0,
          overflow: 'hidden',
          width: `${widthFor(mode) + (showPinned ? PINNED_WIDTH : 0)}px`,
          background: 'var(--bg-elevated)',
          boxShadow: 'var(--shadow-sm)',
          display: 'flex',
          flexDirection: showPinned ? 'row' : 'column',
          flexShrink: 0,
          borderRight: '1px solid var(--border-light)',
        }}
      >
        {showPinned ? (
          <>
            <div style={{ width: `${widthFor(mode)}px`, flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              {modeToggle}
              {railContent}
            </div>
            {pinnedColumn}
          </>
        ) : (
          <>
            {modeToggle}
            {isRail ? railContent : sidebarContent}
          </>
        )}
      </aside>
    );
  }

  // Legacy Mode: Full wrapper with mobile support
  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      {/* Mobile Header */}
      {isMobile && (
        <header style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: '64px',
          background: 'var(--bg-elevated)',
          boxShadow: 'var(--shadow-sm)',
          display: 'flex',
          alignItems: 'center',
          padding: '0 16px',
          zIndex: 1000,
        }}>
          <button
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle navigation menu"
            aria-expanded={isOpen}
            style={{
              width: '44px',
              height: '44px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              borderRadius: '8px',
              transition: 'background 0.2s',
            }}
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          <h1 style={{
            margin: '0 0 0 12px',
            fontSize: '18px',
            fontWeight: 'bold',
            background: 'linear-gradient(135deg, var(--brand-gradient-start) 0%, var(--brand-gradient-end) 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            TeleTime Solutions
          </h1>
        </header>
      )}

      {/* Mobile Overlay */}
      {isMobile && isOpen && (
        <div
          onClick={() => setIsOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'var(--overlay-bg)',
            zIndex: 1001,
            transition: 'opacity 0.3s ease',
          }}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        role="navigation"
        aria-label="Main navigation"
        aria-hidden={isMobile && !isOpen}
        style={{
          position: isMobile ? 'fixed' : 'sticky',
          top: isMobile ? 0 : 0,
          left: 0,
          height: '100vh',
          width: '260px',
          background: 'var(--bg-elevated)',
          boxShadow: 'var(--shadow-sm)',
          transform: isMobile ? (isOpen ? 'translateX(0)' : 'translateX(-100%)') : 'none',
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          zIndex: 1002,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Desktop Logo */}
        {!isMobile && (
          <div style={{
            padding: '20px',
            borderBottom: '1px solid var(--border-light)',
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
          }}>
            <h1 style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: 'bold',
              background: 'linear-gradient(135deg, var(--brand-gradient-start) 0%, var(--brand-gradient-end) 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
              TeleTime Solutions
            </h1>
            <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
              Quotation Management
            </div>
          </div>
        )}

        {/* Mobile spacer */}
        {isMobile && <div style={{ height: '64px' }} />}

        {sidebarContent}
      </aside>

      {/* Main Content */}
      <main
        role="main"
        aria-label="Main content"
        style={{
          flex: 1,
          marginTop: isMobile ? '64px' : 0,
          minHeight: isMobile ? 'calc(100vh - 64px)' : '100vh',
          background: 'var(--bg-secondary)',
        }}
      >
        {children}
      </main>

      {/* Pulse animation for badge */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
      `}</style>
    </div>
  );
};

export default Sidebar;
